import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import TagValueViewer from 'metadata/TagValueViewer';
import NoSampleLabel from 'notebook/data-tables/NoSampleLabel';
import ValidationIcon from 'results/validation/ValidationIcon';
import * as TagUtil from 'schemas/tags/TagUtil';
import Tooltip from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as RichObjectTable from 'ui-library/RichObjectTable';
import {TableCollapserContext} from 'ui-library/tables/TableCollapserContext';
import {readableTime} from 'util/DateUtil';

import {STATUS_TO_NAME} from '../ValidationConstants';
import {ResultStatusTabRowShape, ResultStatusTabTableShape} from './ResultStatusTabConstants';

const STATUS_HEADER_CELL = 'Status';

interface RowProps {
  row: ResultStatusTabRowShape;
}
function Row({row}: RowProps): JSX.Element {
  const status = row.lastAssayValidationChange.status;
  const Cell = RichObjectTable.Cell;
  return (
    // TODO(AMS 11/29/18): It would be preferable to render the label for the primary link field tag via the
    // same mechanism that data tables use to do so -- getRowLabelsFor{File/Batch}DataTable in
    // client/notebook/data-tables/DataTableUtil.js. This will require a bit of refactoring, described in
    // https://github.com/benchling/aurelia/pull/1435#discussion_r237689810.
    <RichObjectTable.Row>
      {row.primaryLinkFieldTag && (
        <Cell>
          {TagUtil.tagHasLink(row.primaryLinkFieldTag) ? (
            <TagValueViewer tag={row.primaryLinkFieldTag} chipSize={ChipSize.TABLE_CELL} />
          ) : (
            <NoSampleLabel />
          )}
        </Cell>
      )}
      <Cell data-test-component="ResultStatusTabTableCell" data-test-element="field">
        {row.fieldName}
      </Cell>
      <Cell data-test-component="ResultStatusTabTableCell" data-test-element="value">
        {row.formattedValue}
      </Cell>
      <Cell data-test-component="ResultStatusTabTableCell" data-test-element="status">
        <Tooltip title={STATUS_TO_NAME[status]}>
          <ValidationIcon status={status} />
        </Tooltip>
      </Cell>
      <Cell data-test-component="ResultStatusTabTableCell" data-test-element="comment">
        {row.lastAssayValidationChange.comment}
      </Cell>
      <Cell>
        <RichObjectTable.UserCell user={row.lastAssayValidationChange.creator} />
      </Cell>
      <Cell>{readableTime(row.lastAssayValidationChange.createdAt)}</Cell>
    </RichObjectTable.Row>
  );
}

interface TableProps {
  table: ResultStatusTabTableShape;
}
export default class ResultStatusTabTable extends React.Component<TableProps> {
  getHeaderCells(): Array<string> {
    return (this.tableHasPrimaryLinkField() ? ['Sample'] : []).concat([
      'Field',
      'Value',
      STATUS_HEADER_CELL,
      'Comment',
      'User',
      'Timestamp',
    ]);
  }

  _getColumns(): Array<JSX.Element> {
    return this.getHeaderCells().map((headerCell) => (
      <col
        key={headerCell}
        className={css(headerCell === STATUS_HEADER_CELL ? styles.statusColumn : styles.nonStatusColumn)}
      />
    ));
  }

  tableHasPrimaryLinkField(): boolean {
    return this.props.table.rows.some((row) => Boolean(row.primaryLinkFieldTag));
  }

  render(): JSX.Element {
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
      <TableCollapserContext.Provider value={{isTableInitiallyCollapsed: false}}>
        <RichObjectTable.Table
          columns={this._getColumns()}
          largerHeader={true}
          headerCells={this.getHeaderCells()}
          leftHeader={this.props.table.tableName}
          // Fixes the fact that the larger default padding/margin of rich object tables results in a
          // horizontal scrollbar appearing on the status tab page even though there's nothing to scroll to
          useMinimalMargin={true}
        >
          {this.props.table.rows.map((row, index) => (
            <Row row={row} key={index} />
          ))}
        </RichObjectTable.Table>
      </TableCollapserContext.Provider>
    );
  }
}

const styles = StyleSheet.create({
  statusColumn: {
    // Narrower than other columns since it's only 'Status' header and check/X icon. However, note that col
    // width is only a min-width. Ideally this'd be the max-width of the column too so that the status column
    // is always 60px, but the CSS didn't work:
    // https://benchling.slack.com/archives/C7X6N0QNN/p1553018044006400
    width: 60,
  },
  nonStatusColumn: {
    // So that the whole table will fit without horizontal scrolling when using split workspace
    width: RichObjectTable.CONTENT_COLUMN_MIN_WIDTH / 2,
  },
});
